import { Alert, Button, Space } from 'antd'
import { planningPreparationSchema } from 'features/libra/inspectionPlanning/planningPreparationConfig'
import {
  InspectionArea,
  PlanningAreasConfig,
  sourceZonesSchema,
} from 'features/libra/inspectionPlanning/planningTypes'
import { techniqueCatalogueSchema } from 'features/libra/inspectionPlanning/techniqueTypes'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { useTicketOntologyFields } from 'features/libra/ontology/useOntologyFields'
import { documentAttachmentIdsSchema } from 'features/libra/sections/documentAttachmentsSection/documentAttachmentsTypes'
import { getVisibleSectionIds } from 'features/libra/stepComponents/conditionalForm/conditionalFormSection/conditionalFormVisibility'
import { useLibraAssetModelData } from 'features/libra/stepComponents/useLibraAssetModelData'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoLocale } from 'features/locale'
import { useRolodexEntries, useRolodexSignedURLs } from 'features/rolodex'
import { useState } from 'react'
import { RolodexEntryType, RolodexFilePath } from 'types'
import { downloadBlob } from 'utils/download'

import { buildPlanningWorkPack, WorkPackInputs } from './buildPlanningWorkPack'
import { buildWorkPackDrawings, loadWorkPackImages } from './workPackDrawings'
import { WorkPackConfig } from './workPackTypes'

export const PlanningWorkPackExport = ({
  config,
  exportConfig,
  areas,
  fieldValuesOverride,
}: {
  config: PlanningAreasConfig
  exportConfig: WorkPackConfig
  areas: InspectionArea[]
  fieldValuesOverride?: FieldValues
}) => {
  const { measurementSystem } = useGeckoLocale()
  const workflow = useWorkflow()
  const {
    fieldValues: savedFields,
    ticketId,
    ticketVersion,
    isLoading,
  } = useTicketAccess()
  const fieldValues = fieldValuesOverride ?? savedFields
  const { ontologyFields } = useTicketOntologyFields()
  const { unit, siteName } = useLibraAssetModelData()
  const preparation = planningPreparationSchema.safeParse(
    workflow.steps.find((step) => step.id === exportConfig.preparationStepId)
      ?.config
  )
  const execution = exportConfig.executionStepId
    ? planningPreparationSchema.safeParse(
        workflow.steps.find((step) => step.id === exportConfig.executionStepId)
          ?.config
      ).data
    : undefined
  const visibleExecutionSections = execution
    ? getVisibleSectionIds(execution.form, fieldValues)
    : []
  const attachmentIds = documentAttachmentIdsSchema
    .array()
    .safeParse(
      execution?.sections.flatMap((section) =>
        section.type === 'document_attachments' &&
        visibleExecutionSections.includes(section.id)
          ? [unwrapFieldValue(fieldValues[section.field]) ?? []]
          : []
      ) ?? []
    )
    .data?.flat()
  const zones = sourceZonesSchema.safeParse(
    unwrapFieldValue(fieldValues[config.sourceZonesField])
  ).data
  const catalogue = techniqueCatalogueSchema.safeParse(
    workflow.globalContext[config.techniqueCatalogueContextKey]
  ).data
  const drawings =
    zones && catalogue ? buildWorkPackDrawings(zones, areas, catalogue) : []
  const ids = [
    ...new Set([
      ...drawings.map((drawing) => drawing.id),
      ...(attachmentIds ?? []),
    ]),
  ]
  const { entries, isLoading: loadingEntries } = useRolodexEntries(
    ids.length ? { ids, type: RolodexEntryType.REPORT } : undefined
  )
  const queries: RolodexFilePath[] = entries.flatMap((entry) =>
    drawings.some((drawing) => drawing.id === entry.id) &&
    typeof entry.data.filename === 'string'
      ? [{ entry_id: entry.id, path: entry.data.filename }]
      : []
  )
  const { results, isLoading: loadingUrls } = useRolodexSignedURLs(
    queries.length ? queries : undefined
  )
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string>()
  if (
    !zones ||
    !catalogue ||
    !attachmentIds ||
    (exportConfig.executionStepId && !execution)
  )
    return null
  const input: WorkPackInputs | undefined = preparation.success
    ? {
        config,
        execution,
        documentNames: Object.fromEntries(
          entries.map((entry) => [
            entry.id,
            String(entry.data.visible_name ?? entry.data.filename ?? entry.id),
          ])
        ),
        measurementSystem,
        exportConfig,
        preparation: preparation.data,
        fieldValues,
        globalContext: workflow.globalContext,
        ontologyFields,
        areas,
        ticketId,
        version: ticketVersion,
        assetName:
          typeof unit?.data.name === 'string'
            ? unit.data.name
            : (unit?.id ?? ticketId),
        siteName,
        generatedAt: new Date().toISOString(),
      }
    : undefined
  const generate = async () => {
    if (generating) return
    setGenerating(true)
    setError(undefined)
    try {
      if (!input)
        throw new Error('The configured preparation step is unavailable.')
      const pack = buildPlanningWorkPack(input)
      const files = entries.flatMap((entry) => {
        const url = results.find(
          (result) => result.entry_id === entry.id
        )?.signed_url
        return url
          ? [
              {
                id: entry.id,
                name: String(
                  entry.data.visible_name ?? entry.data.filename ?? entry.id
                ),
                url,
              },
            ]
          : []
      })
      const images = await loadWorkPackImages(pack.drawings, files)
      const { renderPlanningWorkPack } =
        await import('./RenderPlanningWorkPack')
      const blob = await renderPlanningWorkPack(pack, images)
      await downloadBlob(blob, pack.filename)
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Could not export the planning work pack. Please retry.'
      )
    } finally {
      setGenerating(false)
    }
  }
  return (
    <Space orientation='vertical'>
      <Button
        onClick={generate}
        loading={generating}
        disabled={isLoading || loadingEntries || loadingUrls}>
        {exportConfig.buttonLabel}
      </Button>
      {error && <Alert type='error' title={error} />}
    </Space>
  )
}
